// src/core/roller.js
export function MotionFlowRoller(userOptions = {}) {
  let items = [];
  let ticking = false;
  let initialized = false;

  let scrollHandler = null;
  let resizeHandler = null;

  const DIGIT_STEP_DELAY = 200;

  /* ==========================================================
     ACCESSIBILITY
  ========================================================== */
  const reduceMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;

  /* ==========================================================
     DEFAULTS (ROLLER ONLY)
  ========================================================== */
  const defaults = {
    from: 0,
    once: false,
    repeat: "both",
    duration: 1200,
    trigger: "top 90%",
  };

  const cfg = {
    ...defaults,
    ...(userOptions.roller || {}),
  };

  /* ==========================================================
     HELPERS
  ========================================================== */
  function parseTrigger(str) {
    if (!str) return { elementAnchor: "top", viewportAnchor: 90 };

    const parts = String(str).trim().toLowerCase().split(/\s+/);
    const map = { top: 0, center: 50, bottom: 100 };

    return {
      elementAnchor: parts[0] || "top",
      viewportAnchor: parts[1]?.endsWith("%")
        ? parseInt(parts[1], 10)
        : map[parts[1]] ?? 90,
    };
  }

  function getAnchor(rect, anchor) {
    if (anchor === "center") return rect.top + rect.height / 2;
    if (anchor === "bottom") return rect.bottom;
    return rect.top;
  }

  function extractDigits(str) {
    return String(str || "").replace(/[^\d]/g, "");
  }

  function toNumberSafe(str) {
    const d = extractDigits(str);
    return d ? parseInt(d, 10) : 0;
  }

  function digitLen(n) {
    const s = String(Math.abs(n));
    return s.length || 1;
  }

  /* ==========================================================
     DOM BUILD
  ========================================================== */
  function buildRoller(el, totalDigits) {
    const wrapper = document.createElement("span");
    wrapper.className = "mf-roller";

    for (let i = 0; i < totalDigits; i++) {
      const digit = document.createElement("span");
      digit.className = "mf-roller-digit";
      digit.style.display = "inline-block";
      digit.style.overflow = "hidden";

      const inner = document.createElement("span");
      inner.className = "mf-roller-digit-inner";
      inner.style.display = "block";
      inner.style.willChange = "transform";

      for (let d = 0; d <= 9; d++) {
        const line = document.createElement("span");
        line.textContent = d;
        line.style.display = "block";
        inner.appendChild(line);
      }

      digit.appendChild(inner);
      wrapper.appendChild(digit);
    }

    el.innerHTML = "";
    el.appendChild(wrapper);
  }

  function setVisibleDigits(el, totalDigits, visibleDigits) {
    const digits = el.querySelectorAll(".mf-roller-digit");
    const hideCount = Math.max(0, totalDigits - visibleDigits);

    digits.forEach((digitEl, idx) => {
      if (idx < hideCount) {
        digitEl.style.width = "0";
        digitEl.style.opacity = "0";
      } else {
        digitEl.style.width = "";
        digitEl.style.opacity = "";
      }
    });
  }

  function setValue(el, value, totalDigits, visibleDigits, immediate = false) {
    const inners = el.querySelectorAll(".mf-roller-digit-inner");
    const startIndex = Math.max(0, totalDigits - visibleDigits);

    let s = String(Math.abs(value));
    if (s.length > visibleDigits) s = s.slice(-visibleDigits);
    else s = s.padStart(visibleDigits, "0");

    for (let i = 0; i < visibleDigits; i++) {
      const inner = inners[startIndex + i];
      if (!inner) continue;

      const n = parseInt(s[i], 10);

      if (immediate) {
        inner.style.transition = "none";
        inner.style.transform = `translateY(-${n * 10}%)`;
        inner.offsetHeight;
        inner.style.transition = "";
      } else {
        inner.style.transform = `translateY(-${n * 10}%)`;
      }
    }
  }

  /* ==========================================================
     ANIMATION
  ========================================================== */
  function easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  function stopItem(item) {
    item.animating = false;
    if (item.rafId) cancelAnimationFrame(item.rafId);
    if (item.digitTimer) clearInterval(item.digitTimer);
    item.rafId = null;
    item.digitTimer = null;
    item.startTime = null;
  }

  function animate(item) {
    if (item.animating) return;

    stopItem(item);
    item.animating = true;

    let currentVisible = item.initialDigits;
    const targetVisible = item.targetDigits;
    let lastValue = item.from;

    if (reduceMotion) {
      setVisibleDigits(item.el, item.totalDigits, targetVisible);
      setValue(item.el, item.to, item.totalDigits, targetVisible, true);
      item.animating = false;
      return;
    }

    if (currentVisible !== targetVisible) {
      item.digitTimer = setInterval(() => {
        currentVisible += currentVisible < targetVisible ? 1 : -1;
        setVisibleDigits(item.el, item.totalDigits, currentVisible);
        setValue(item.el, lastValue, item.totalDigits, currentVisible, true);

        if (currentVisible === targetVisible) {
          clearInterval(item.digitTimer);
          item.digitTimer = null;
        }
      }, DIGIT_STEP_DELAY);
    }

    function step(ts) {
      if (!item.startTime) item.startTime = ts;

      const p = Math.min((ts - item.startTime) / item.duration, 1);
      const value = Math.round(
        item.from + (item.to - item.from) * easeOutCubic(p)
      );

      lastValue = value;
      setValue(item.el, value, item.totalDigits, currentVisible);

      if (p < 1) {
        item.rafId = requestAnimationFrame(step);
      } else {
        stopItem(item);
        setVisibleDigits(item.el, item.totalDigits, targetVisible);
        setValue(item.el, item.to, item.totalDigits, targetVisible, true);
      }
    }

    item.rafId = requestAnimationFrame(step);
  }

  /* ==========================================================
     UPDATE
  ========================================================== */
  function update() {
    const vh = window.innerHeight;

    items.forEach(item => {
      const rect = item.el.getBoundingClientRect();
      const elementPos = getAnchor(rect, item.trigger.elementAnchor);
      const viewportPos = (vh * item.trigger.viewportAnchor) / 100;

      const visible = elementPos < viewportPos && rect.bottom > 0;
      const above = rect.bottom <= 0;
      const below = rect.top >= vh;

      if (visible && !item.started) {
        item.started = true;
        animate(item);
      }

      if (!item.once && item.started) {
        const reset =
          (item.repeat === "top" && above) ||
          (item.repeat === "bottom" && below) ||
          (item.repeat === "both" && (above || below));

        if (reset) {
          item.started = false;
          stopItem(item);
          setVisibleDigits(item.el, item.totalDigits, item.initialDigits);
          setValue(item.el, item.from, item.totalDigits, item.initialDigits, true);
        }
      }
    });

    ticking = false;
  }

  /* ==========================================================
     LIFECYCLE (IDEMPOTENT)
  ========================================================== */
  function init() {
    if (initialized) {
      destroy(false);
    }

    initialized = true;
    items = [];

    document.querySelectorAll("[data-mf-roller-to]").forEach(el => {
      const raw = el.getAttribute("data-mf-roller-to");
      if (!raw) return;

      const target = toNumberSafe(raw);
      const fromRaw = el.dataset.mfRollerFrom;
      const from =
        fromRaw !== undefined
          ? toNumberSafe(fromRaw)
          : cfg.from ?? 0;


      const targetDigits = digitLen(target);
      const fromDigits = digitLen(from);
      const totalDigits = Math.max(fromDigits, targetDigits);

      buildRoller(el, totalDigits);
      setVisibleDigits(el, totalDigits, fromDigits);
      setValue(el, from, totalDigits, fromDigits, true);

      items.push({
        el,
        from,
        to: target,
        duration: Number(el.dataset.mfRollerDuration ?? cfg.duration),
        trigger: parseTrigger(el.dataset.mfRollerTrigger ?? cfg.trigger),
        once:
          el.dataset.mfRollerOnce !== undefined
            ? el.dataset.mfRollerOnce === "true" ||
              el.dataset.mfRollerOnce === ""
            : cfg.once,
        repeat: String(el.dataset.mfRollerRepeat ?? cfg.repeat).toLowerCase(),
        initialDigits: fromDigits,
        targetDigits,
        totalDigits,
        started: false,
        animating: false,
        startTime: null,
        rafId: null,
        digitTimer: null,
      });
    });

    if (!items.length) return;

    update();

    scrollHandler = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    resizeHandler = scrollHandler;

    window.addEventListener("scroll", scrollHandler, { passive: true });
    window.addEventListener("resize", resizeHandler);
  }

  function refresh() {
    update();
  }

  function destroy(reset = true) {
    initialized = false;

    window.removeEventListener("scroll", scrollHandler);
    window.removeEventListener("resize", resizeHandler);

    items.forEach(item => {
      stopItem(item);
      if (reset) {
        item.el.innerHTML = "";
        item.el.textContent = String(item.from);
      }
    });

    items = [];
    ticking = false;
  }

  init();
  return { init, refresh, destroy };
}
